// AppConfigContext.tsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import { getApplicationConfig } from "../network/apiService";
import { getCookie } from "../utils/cookies";
import eventEmitter from "../eventEmitter";
import {
  clearCachedConfig,
  readCachedConfig,
  writeCachedConfig,
  type ApplicationConfig
} from "../application-config";
import { isFeatureEnabled as isFlagEnabled } from "../feature-flags/featureFlagHelpers";
import type { FeatureFlagId } from "../feature-flags/featureFlags";

interface AppConfigContextType {
  config: ApplicationConfig | null;
  loading: boolean;
  error: Error | null;
  isFeatureEnabled: (flag: FeatureFlagId) => boolean;
}

const defaultIsFeatureEnabled = (_flag: FeatureFlagId) => false;

const AppConfigContext = createContext<AppConfigContextType>({
  config: null,
  loading: false,
  error: null,
  isFeatureEnabled: defaultIsFeatureEnabled
});

export const useAppConfig = () => useContext(AppConfigContext);

function getInitialConfig(): ApplicationConfig | null {
  if (!getCookie("id_token")) {
    return null;
  }
  return readCachedConfig();
}

export const AppConfigProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [config, setConfig] = useState<ApplicationConfig | null>(
    getInitialConfig
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const inFlightRef = useRef(false);
  const loadedRef = useRef(config != null);

  const fetchConfig = useCallback(async (force = false) => {
    if (!getCookie("id_token") || inFlightRef.current) {
      return;
    }
    if (!force && loadedRef.current) {
      return;
    }

    inFlightRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const res: any = await getApplicationConfig();
      const nextConfig: ApplicationConfig = res?.data ?? {};
      setConfig(nextConfig);
      writeCachedConfig(nextConfig);
      loadedRef.current = true;
    } catch (err: any) {
      setError(err);
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Always refetch on hard reload / authenticated SPA entry so flags stay current.
    fetchConfig(true);

    // Cover login race: AuthCallback sets id_token then emits userLogin.
    const onUserLogin = () => {
      loadedRef.current = false;
      clearCachedConfig();
      fetchConfig(true);
    };

    eventEmitter.addEventListener("userLogin", onUserLogin);
    return () => {
      eventEmitter.removeEventListener("userLogin", onUserLogin);
    };
  }, [fetchConfig]);

  const isFeatureEnabled = useCallback(
    (flag: FeatureFlagId) => isFlagEnabled(config, flag),
    [config]
  );

  return (
    <AppConfigContext.Provider
      value={{ config, loading, error, isFeatureEnabled }}
    >
      {children}
    </AppConfigContext.Provider>
  );
};
